import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import back_arrow from "../../assets/svg/back-arrow.svg";
import BillingAutomationHeader from "./BillingAutomationHeader";
import "../../styles/BillingAutomation/BillingAutomationViewpage.css";
import CommonFooter from "../../CommonFooter/CommonFooter";
import billingAutomationReceiptIcon from "../../assets/svg/billing-automation-receipt-icon.svg";
import noteIcon from "../../assets/svg/note-icon.svg";
import { FiMail } from "react-icons/fi";
import axiosInstance from "../../axiosInstance";
import { ToastContainer, toast } from "react-toastify";


const MetaStrip = ({ lineRemarks, customerRemarks, billingNature }) => (
  <div className="billing-automation-viewpage-invoice-meta">
    <div className="billing-automation-viewpage-meta-block">
      <div className="billing-automation-viewpage-meta-label">Remarks (line item)</div>
      <div className="billing-automation-viewpage-meta-value">{lineRemarks}</div>
    </div>
    <div className="billing-automation-viewpage-meta-block">
      <div className="billing-automation-viewpage-meta-label">Remarks to Customer</div>
      <div className="billing-automation-viewpage-meta-value">{customerRemarks}</div>
    </div>
    <div className="billing-automation-viewpage-meta-block">
      <div className="billing-automation-viewpage-meta-label">Billing Nature</div>
      <div className="billing-automation-viewpage-meta-value">{billingNature}</div>
    </div>
  </div>
);

const NOTE_MAP = {
  single:
    "All selected charge lines will be consolidated and generated under a single invoice.",

  separate:
    "Each charge line will be generated as an individual invoice based on configured billing rules.",

  merge_single:
    "Selected charge lines are merged and generated as one consolidated invoice.",

  merge_separate:
    "Selected charge lines are merged by grouping criteria and generated as separate invoices.",
};

const SingleGroupCard = ({ item }) => (
  <div className="billing-automation-viewpage-invoice-item">
    {item.lines.map((line, i) => (
      <div key={line.id}>
        <div className="billing-automation-viewpage-invoice-item-top">
          <div className="billing-automation-viewpage-line-badge">{i + 1}</div>
          <div className="billing-automation-viewpage-invoice-icon">
            <img src={billingAutomationReceiptIcon} alt="Receipt" className="billing-automation-custom-icon" />
          </div>
          <div className="billing-automation-viewpage-invoice-info">
            <p className="billing-automation-viewpage-invoice-name">{line.name}</p>
            <p className="billing-automation-viewpage-invoice-code">
              Charge code : <span className="code-link">{line.chargeCode}</span>
            </p>
          </div>
          <div className="billing-automation-viewpage-invoice-amounts">
            <div className="billing-automation-viewpage-amount-block">
              <span className="billing-automation-viewpage-amount-label">Quantity</span>
              <span className="billing-automation-viewpage-amount-value">{line.quantity}</span>
            </div>
            <div className="billing-automation-viewpage-amount-block">
              <span className="billing-automation-viewpage-amount-label">Rate</span>
              <span className="billing-automation-viewpage-amount-value">{line.rate}</span>
            </div>
            <div className="billing-automation-viewpage-amount-block">
              <span className="billing-automation-viewpage-amount-label">Total</span>
              <span className="billing-automation-viewpage-amount-value">{line.total}</span>
            </div>
          </div>
        </div>

        <MetaStrip
          lineRemarks={line.lineRemarks}
          customerRemarks={line.customerRemarks}
          billingNature={line.billingNature}
        />

        {i < item.lines.length - 1 && (
          <div className="billing-automation-viewpage-dashed-divider" />
        )}
      </div>
    ))}


  </div>
);

const BillingAutomationViewpage = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.id;

  const [activeTab, setActiveTab] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewData, setViewData] = useState(null);

  const handleSearch = (val) => {
    console.log("search:", val);
  };

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        const res = await axiosInstance.get(`/automation/edit/${id}`);
        setViewData(res.data?.data || null);
      } catch {
        toast.error("Failed to load automation details.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const automation = viewData?.automation;
  const jobTypes = viewData?.job_types || [];

  const TYPE_LABEL_MAP = {
    single: "Single Invoice",
    separate: "Separate Invoice",
    merge_single: "Merge & Single Invoice",
    merge_separate: "Merge & Separate Invoice",
  };

  const totalSales = jobTypes
    .flatMap(jt => (jt.charge_lines || []).filter(cl => cl.is_mapped))
    .reduce((sum, cl) => sum + parseFloat(cl.net_total || 0), 0)
    .toLocaleString();

  const allLines = jobTypes.flatMap(jt =>
    (jt.charge_lines || [])
      .filter(cl => cl.is_mapped && cl.card_configs?.length)
      .flatMap(cl =>
        cl.card_configs.map(config => ({
          contract_charge_sales_line_id: cl.contract_charge_sales_line_id,
          description: cl.description,
          charge_code: cl.charge_code,
          unit_of_quantity: cl.unit_of_quantity,
          unit_of_rate: cl.unit_of_rate,
          net_total: cl.net_total,
          remarks: cl.remarks,
          config_remarks: config.remarks,
          billing_nature: config.billing_nature,
          job_type_code: jt.job_type_code,
          job_type_name: jt.job_type_name,
          invoice_type: config.invoice_type,
          invoice_group_no: config.invoice_group_no,
          line_group_no: config.line_group_no,
        }))
      )
  );

  const groupedByType = allLines.reduce((acc, cl) => {
    if (!acc[cl.invoice_type]) acc[cl.invoice_type] = [];
    acc[cl.invoice_type].push(cl);
    return acc;
  }, {});

  const tabs = Object.keys(groupedByType).map(key => {
    let count;
    if (key === "single") {
      const groups = new Set((groupedByType["single"] || []).map(cl => cl.invoice_group_no));
      count = groups.size;
    } else if (key === "separate") {
      count = (groupedByType["separate"] || []).length;
    } else if (key === "merge_single") {
      const pairs = new Set(
        (groupedByType["merge_single"] || []).map(cl => `${cl.invoice_group_no}_${cl.line_group_no}`)
      );
      count = pairs.size;
    } else if (key === "merge_separate") {
      const pairs = new Set(
        (groupedByType["merge_separate"] || []).map(cl => `${cl.invoice_group_no}_${cl.line_group_no}`)
      );
      count = pairs.size;
    } else {
      count = groupedByType[key].length;
    }
    return { key, label: TYPE_LABEL_MAP[key] || key, count };
  });

  useEffect(() => {
    if (tabs.length > 0 && (!activeTab || !tabs.find(t => t.key === activeTab))) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs.length]);

  const activeLines = (() => {
    if (activeTab === "single") {
      const groupMap = {};
      (groupedByType["single"] || []).forEach(cl => {
        const key = cl.invoice_group_no ?? cl.contract_charge_sales_line_id;
        if (!groupMap[key]) groupMap[key] = [];
        groupMap[key].push(cl);
      });
      return Object.values(groupMap).map((group, idx) => ({
        id: `single_group_${group[0].invoice_group_no ?? idx}`,
        lines: group.map(cl => ({
          id: cl.contract_charge_sales_line_id,
          name: cl.description,
          chargeCode: cl.charge_code,
          lineRemarks: cl.remarks || "--",
          quantity: cl.unit_of_quantity,
          rate: cl.unit_of_rate,
          total: cl.net_total,
          customerRemarks: cl.config_remarks || "--",
          billingNature: cl.billing_nature || "--",
        })),
        note: NOTE_MAP["single"],
      }));
    }

    if (activeTab === "separate") {
      return (groupedByType["separate"] || []).map(cl => ({
        id: `separate_${cl.contract_charge_sales_line_id}`,
        lines: [{
          id: cl.contract_charge_sales_line_id,
          name: cl.description,
          chargeCode: cl.charge_code,
          lineRemarks: cl.remarks || "--",
          quantity: cl.unit_of_quantity,
          rate: cl.unit_of_rate,
          total: cl.net_total,
          customerRemarks: cl.config_remarks || "--",
          billingNature: cl.billing_nature || "--",
        }],
        note: NOTE_MAP["separate"],
      }));
    }

    if (activeTab === "merge_single") {
      const cardMap = {};
      (groupedByType["merge_single"] || []).forEach(cl => {
        const key = `${cl.invoice_group_no}_${cl.line_group_no}`;
        if (!cardMap[key]) cardMap[key] = [];
        cardMap[key].push(cl);
      });

      return Object.entries(cardMap).map(([key, lines]) => ({
        id: `merge_single_${key}`,
        invoiceGroupNo: lines[0].invoice_group_no,
        lineGroupNo: lines[0].line_group_no,
        lines: lines.map(cl => ({
          id: cl.contract_charge_sales_line_id,
          name: cl.description,
          chargeCode: cl.charge_code,
          lineRemarks: cl.remarks || "--",
          quantity: cl.unit_of_quantity,
          rate: cl.unit_of_rate,
          total: cl.net_total,
          jobTypeName: cl.job_type_name,
          customerRemarks: cl.config_remarks || "--",
          billingNature: cl.billing_nature || "--",
        })),
        note: NOTE_MAP["merge_single"],
      }));
    }

    if (activeTab === "merge_separate") {
      const invoiceGroupMap = {};
      (groupedByType["merge_separate"] || []).forEach(cl => {
        const igKey = cl.invoice_group_no;
        if (!invoiceGroupMap[igKey]) invoiceGroupMap[igKey] = {};
        const lgKey = cl.line_group_no ?? cl.contract_charge_sales_line_id;
        if (!invoiceGroupMap[igKey][lgKey]) invoiceGroupMap[igKey][lgKey] = [];
        invoiceGroupMap[igKey][lgKey].push(cl);
      });

      const cards = [];
      Object.entries(invoiceGroupMap).forEach(([igNo, lineGroups]) => {
        Object.entries(lineGroups).forEach(([lgNo, lines]) => {
          cards.push({
            id: `merge_separate_${igNo}_${lgNo}`,
            invoiceGroupNo: igNo,
            lineGroupNo: lgNo,
            lines: lines.map(cl => ({
              id: cl.contract_charge_sales_line_id,
              name: cl.description,
              chargeCode: cl.charge_code,
              lineRemarks: cl.remarks || "--",
              quantity: cl.unit_of_quantity,
              rate: cl.unit_of_rate,
              total: cl.net_total,
              jobTypeName: cl.job_type_name,
              customerRemarks: cl.config_remarks || "--",
              billingNature: cl.billing_nature || "--",
            })),
            note: NOTE_MAP["merge_separate"],
          });
        });
      });
      return cards;
    }

    return [];
  })();


  return (
    <>
      <BillingAutomationHeader onSearch={handleSearch} />

      {/* Page header */}
      <div className="common-cost-creation-header">
        <div className="common-cost-creation-header-left">
          <div className="common-cost-back-btn" onClick={() => navigate(-1)}>
            <img src={back_arrow} alt="Back Arrow" />
          </div>
          <p className="new_job" style={{ fontSize: "15px" }}>
            {isLoading ? "Loading..." : automation?.automation_code || "Automation"}
          </p>
        </div>
      </div>

      <div className="billing-automation-viewpage-container">
        <div className="billing-automation-viewpage-details-card">

          {/* Left column */}
          <div className="billing-automation-viewpage-details-left">
            <p className="billing-automation-viewpage-details-title">Automation details</p>

            <div className="billing-automation-viewpage-details-row">
              <span className="billing-automation-viewpage-details-label">Contract Code</span>
              <span className="billing-automation-viewpage-details-value">
                {automation?.contract_code || "--"}
              </span>
            </div>

            <div className="billing-automation-viewpage-details-row">
              <span className="billing-automation-viewpage-details-label">Project</span>
              <span className="billing-automation-viewpage-details-value">
                {automation?.project_code} - {automation?.project_name}
              </span>
            </div>

            <div className="billing-automation-viewpage-details-row">
              <span className="billing-automation-viewpage-details-label">Start &amp; End Date</span>
              <span className="billing-automation-viewpage-details-value">
                {automation?.start_date || "--"}&nbsp;-&nbsp;
                <span className="end-date">{automation?.end_date || "--"}</span>
              </span>
            </div>

            <div className="billing-automation-viewpage-details-row">
              <span className="billing-automation-viewpage-details-label">Mail ID</span>
              <div className="billing-automation-creation-info-chips">
                {automation?.contract_mail
                  ? automation.contract_mail.split(',').map((mail, i) => (
                    <span key={i} className="billing-automation-info-chip billing-automation-info-chip--mail">
                      <FiMail style={{ marginRight: '5px' }} />
                      {mail.trim()}
                    </span>
                  ))
                  : <span>--</span>
                }
              </div>
            </div>
          </div>

          {/* Right column – Job Types */}
          <div className="billing-automation-viewpage-details-right">
            <div className="billing-automation-viewpage-job-types-box">
              <p className="billing-automation-viewpage-job-types-title">Job Types</p>
              <div className="billing-automation-viewpage-job-types-tags">
                {jobTypes.map(jt => <span key={jt.job_type_id} className="billing-automation-viewpage-job-tag">{jt.job_type_code} - {jt.job_type_name}</span>)}
              </div>
            </div>
          </div>
        </div>

        <div className="billing-automation-viewpage-tabs-card">

          {/* Tab header */}
          <div className="billing-automation-viewpage-tabs-header">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`billing-automation-viewpage-tab-btn ${activeTab === tab.key ? "active" : ""
                  }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="billing-automation-viewpage-tab-count">{tab.count}</span>
              </button>
            ))}

            <span className="billing-automation-viewpage-total-sales">
              <span>Total Sales :</span> {totalSales}
            </span>
          </div>

          {/* Tab content */}
          {/* Tab content */}
          <div className="billing-automation-viewpage-tab-content">
            {activeLines.length === 0 && activeTab !== null ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF' }}>
                No records found.
              </div>
            ) : activeTab === "merge_single" ? (
              activeLines.map((item, index) => (
                <div key={item.id} style={{ marginBottom: '12px' }}>
                  <div style={{
                    fontSize: '11px', fontWeight: 600,
                    marginBottom: '8px', paddingLeft: '4px',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    {item.lines.length > 1 ? (
                      <span style={{
                        backgroundColor: '#DCFCE7', color: '#16A34A',
                        borderRadius: '999px', padding: '1px 8px', fontSize: '10px'
                      }}>
                        Combined ({item.lines.length} lines items)
                      </span>
                    ) : (
                      <span style={{
                        backgroundColor: '#F3F4F6', color: '#6B7280',
                        borderRadius: '999px', padding: '1px 8px', fontSize: '10px'
                      }}>
                        Standalone lines items
                      </span>
                    )}
                  </div>
                  <SingleGroupCard item={item} />
                </div>
              ))
            ) : (
              activeLines.map((item, index) => (
                <div key={item.id} style={{ marginBottom: '12px' }}>
                  {activeTab === "merge_separate" && (
                    <div style={{
                      fontSize: '11px', fontWeight: 600,
                      marginBottom: '4px', paddingLeft: '4px',
                      display: 'flex', alignItems: 'center', gap: '6px'
                    }}>
                      {item.lines.length > 1 ? (
                        <span style={{
                          backgroundColor: '#DCFCE7', color: '#16A34A',
                          borderRadius: '999px', padding: '1px 8px', fontSize: '10px'
                        }}>
                          Combined ({item.lines.length} lines items)
                        </span>
                      ) : (
                        <span style={{
                          backgroundColor: '#F3F4F6', color: '#6B7280',
                          borderRadius: '999px', padding: '1px 8px', fontSize: '10px'
                        }}>
                          Standalone lines items
                        </span>
                      )}
                    </div>
                  )}
                  <SingleGroupCard item={item} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <CommonFooter />
    </>
  );
};

export default BillingAutomationViewpage;